import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Eye, FileSpreadsheet, Search, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtIDR, fmtDateShort } from "../lib/api";
import { Panel } from "../lib/ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const MPS = [{ value: "all", label: "Semua Marketplace" }, { value: "shopee", label: "Shopee" }, { value: "tiktok", label: "TikTok Shop" }];

const STATUS_STYLE = {
  "Rugi": "bg-red-100 text-red-700",
  "<25%": "bg-amber-100 text-amber-700",
  "25-40%": "bg-blue-100 text-blue-700",
  ">=40%": "bg-emerald-100 text-emerald-700",
};

const Stat = ({ label, value, tone = "text-cocoa" }) => (
  <div className="rounded-lg border border-taupe/20 bg-white px-3 py-2 min-w-[130px]">
    <div className="text-[11px] uppercase tracking-wide text-wmuted">{label}</div>
    <div className={`text-base font-bold ${tone}`}>{value}</div>
  </div>
);

function TxnDialog({ open, onClose, filters, row }) {
  const [data, setData] = useState({ items: [], count: 0 });
  useEffect(() => {
    if (!open || !row) return;
    api.get("/finance/recon/report/transactions", {
      params: {
        product_base: row.product_base, color: row.color || "",
        marketplace: filters.marketplace, store: "all",
        date_from: filters.date_from || undefined, date_to: filters.date_to || undefined,
        basis: filters.basis, group: filters.group,
      },
    }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [open, row, filters]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl" data-testid="recon-txn-dialog">
        <DialogHeader>
          <DialogTitle>{row?.product_base} {row?.color ? `· ${row.color}` : ""}</DialogTitle>
          <DialogDescription>{data.count} transaksi sumber (Order ID · kategori · HPP dari master ERP)</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Order ID</TableHead><TableHead>Kategori</TableHead><TableHead>No. Sale</TableHead>
                <TableHead className="text-right">Nett</TableHead><TableHead className="text-right">HPP</TableHead>
                <TableHead className="text-right">Sisa</TableHead><TableHead>HPP Src</TableHead><TableHead>Tgl Cair</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-wmuted">Tidak ada transaksi.</TableCell></TableRow>
              ) : data.items.map((t) => (
                <TableRow key={t.id} data-testid={`recon-txn-${t.id}`}>
                  <TableCell className="text-sm font-mono whitespace-nowrap">{t.txn_id}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px] uppercase">{t.category}</Badge></TableCell>
                  <TableCell className="text-sm text-cocoa">{t.matched_sale_number || "-"}</TableCell>
                  <TableCell className="text-right text-sm">{fmtIDR(t.net)}</TableCell>
                  <TableCell className="text-right text-sm text-wmuted">{fmtIDR(t.hpp)}</TableCell>
                  <TableCell className={`text-right text-sm font-semibold ${t.sisa >= 0 ? "text-status-passed" : "text-red-500"}`}>{fmtIDR(t.sisa)}</TableCell>
                  <TableCell>{t.hpp_source === "erp" ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">ERP</Badge> : <Badge className="bg-amber-100 text-amber-700 text-[10px]">tidak ketemu</Badge>}</TableCell>
                  <TableCell className="text-sm text-wmuted whitespace-nowrap">{fmtDateShort(t.settlement_released_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ReconOverhaulTab() {
  const [marketplace, setMarketplace] = useState("all");
  const [basis, setBasis] = useState("released");
  const [group, setGroup] = useState("product_color");
  const [dfrom, setDfrom] = useState("");
  const [dto, setDto] = useState("");
  const [q, setQ] = useState("");
  const [report, setReport] = useState({ items: [], totals: {} });
  const [batches, setBatches] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [drill, setDrill] = useState(null);
  const fileRef = useRef();

  const filters = useMemo(() => ({ marketplace, basis, group, date_from: dfrom, date_to: dto }), [marketplace, basis, group, dfrom, dto]);

  const loadReport = useCallback(() => {
    api.get("/finance/recon/report", {
      params: { marketplace, store: "all", date_from: dfrom || undefined, date_to: dto || undefined, basis, group, q: q || undefined },
    }).then((r) => setReport(r.data)).catch(() => {});
  }, [marketplace, dfrom, dto, basis, group, q]);

  const loadBatches = useCallback(() => {
    api.get("/finance/recon/batches").then((r) => setBatches(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(loadReport, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [loadReport, q]);
  useEffect(() => { loadBatches(); }, [loadBatches]);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("store", "");
      const r = await api.post("/finance/recon/upload", fd);
      toast.success(`Berhasil: ${r.data.row_total} baris (baru ${r.data.inserted}, diperbarui ${r.data.updated})`);
      loadReport();
      loadBatches();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const delBatch = async (b) => {
    if (!window.confirm(`Hapus batch "${b.filename}"? Baris rekonsiliasinya ikut terhapus. Bisa diupload ulang.`)) return;
    try {
      const r = await api.delete(`/finance/recon/batches/${b.id}`);
      toast.success(`Batch dihapus (${r.data.rows_deleted} baris)`);
      loadReport();
      loadBatches();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const t = report.totals || {};
  const sc = t.status_counts || {};

  return (
    <div className="space-y-4" data-testid="recon-overhaul-tab">
      <Panel>
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div>
            <div className="font-semibold text-cocoa">Rekonsiliasi Pencairan Marketplace (Overhaul)</div>
            <div className="text-xs text-wmuted">Upload file Excel pencairan (sheet CAIRTIKTOK / CAIRSHOPEE). Klasifikasi pencairan/refund/iklan otomatis, dedup per Order ID, HPP dari master ERP.</div>
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={upload} data-testid="recon-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="recon-upload-btn" className="bg-cocoa hover:bg-cocoa-dark text-ivory">
              <Upload className="w-4 h-4 mr-1.5" /> {uploading ? "Memproses..." : "Upload Pencairan"}
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-wmuted" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari produk / warna..." className="w-56 pl-9 bg-white" data-testid="recon-search" />
          </div>
          <Select value={marketplace} onValueChange={setMarketplace}>
            <SelectTrigger className="w-44 bg-white" data-testid="recon-mp-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{MPS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={group} onValueChange={setGroup}>
            <SelectTrigger className="w-40 bg-white" data-testid="recon-group-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="product_color">Produk + Warna</SelectItem>
              <SelectItem value="product">Per Produk</SelectItem>
            </SelectContent>
          </Select>
          <Select value={basis} onValueChange={setBasis}>
            <SelectTrigger className="w-36 bg-white" data-testid="recon-basis-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="released">Tgl Cair</SelectItem>
              <SelectItem value="order">Tgl Pesan</SelectItem>
            </SelectContent>
          </Select>
          <Input type="date" value={dfrom} onChange={(e) => setDfrom(e.target.value)} className="w-36 bg-white" data-testid="recon-date-from" />
          <span className="text-wmuted">—</span>
          <Input type="date" value={dto} onChange={(e) => setDto(e.target.value)} className="w-36 bg-white" data-testid="recon-date-to" />
          {(dfrom || dto) && <Button size="sm" variant="outline" onClick={() => { setDfrom(""); setDto(""); }} className="border-taupe text-cocoa" data-testid="recon-date-reset">Reset</Button>}
        </div>

        <div className="flex gap-3 mb-4 flex-wrap" data-testid="recon-totals">
          <Stat label="Nett Produk" value={fmtIDR(t.net)} tone="text-cocoa" />
          <Stat label="Total HPP (ERP)" value={fmtIDR(t.hpp)} tone="text-wmuted" />
          <Stat label="Sisa (Nett−HPP)" value={fmtIDR(t.sisa)} tone={t.sisa >= 0 ? "text-status-passed" : "text-red-500"} />
          <Stat label="Sisa/HPP %" value={t.agg_pct != null ? `${t.agg_pct}%` : "—"} tone="text-status-passed" />
          <Stat label="Refund" value={fmtIDR(t.refund)} tone="text-red-500" />
          <Stat label="Biaya Iklan" value={fmtIDR(t.iklan)} tone="text-red-500" />
          <Stat label="Kompensasi" value={fmtIDR(t.kompensasi)} tone="text-cocoa" />
          <Stat label="Penyesuaian" value={fmtIDR(t.penyesuaian)} tone="text-cocoa" />
          <Stat label="Grup Produk" value={t.product_groups ?? 0} tone="text-cocoa" />
        </div>
        <div className="flex gap-2 mb-4 flex-wrap text-xs" data-testid="recon-status-counts">
          <Badge className="bg-red-100 text-red-700">Rugi: {sc["Rugi"] || 0}</Badge>
          <Badge className="bg-amber-100 text-amber-700">&lt;25%: {sc["<25%"] || 0}</Badge>
          <Badge className="bg-blue-100 text-blue-700">25-40%: {sc["25-40%"] || 0}</Badge>
          <Badge className="bg-emerald-100 text-emerald-700">≥40%: {sc[">=40%"] || 0}</Badge>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>MP</TableHead><TableHead>Produk</TableHead>
                {group === "product_color" && <TableHead>Warna</TableHead>}
                <TableHead className="text-right">Order</TableHead>
                <TableHead className="text-right">Nett</TableHead><TableHead className="text-right">Refund</TableHead>
                <TableHead className="text-right">HPP</TableHead><TableHead className="text-right">Sisa</TableHead>
                <TableHead className="text-right">Sisa/HPP</TableHead><TableHead className="text-right">Kurang→25%</TableHead>
                <TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {report.items.length === 0 ? (
                <TableRow><TableCell colSpan={12} className="text-center py-10 text-wmuted">Belum ada data. Upload file pencairan untuk mulai.</TableCell></TableRow>
              ) : report.items.map((r, i) => (
                <TableRow key={`${r.marketplace}-${r.product_base}-${r.color}-${i}`} data-testid={`recon-row-${i}`}>
                  <TableCell><Badge variant="outline" className="text-[10px] uppercase">{r.marketplace}</Badge></TableCell>
                  <TableCell className="text-sm font-medium text-cocoa max-w-[200px] truncate" title={r.product_base}>{r.product_base}</TableCell>
                  {group === "product_color" && <TableCell className="text-sm">{r.color || "-"}</TableCell>}
                  <TableCell className="text-right text-sm">{r.orders}</TableCell>
                  <TableCell className="text-right text-sm">{fmtIDR(r.net)}</TableCell>
                  <TableCell className="text-right text-sm text-red-500">{r.refund ? fmtIDR(r.refund) : "-"}</TableCell>
                  <TableCell className="text-right text-sm text-wmuted">{fmtIDR(r.hpp)}</TableCell>
                  <TableCell className={`text-right text-sm font-semibold ${r.sisa >= 0 ? "text-status-passed" : "text-red-500"}`}>{fmtIDR(r.sisa)}</TableCell>
                  <TableCell className="text-right text-sm">{r.sisa_pct != null ? `${r.sisa_pct}%` : "—"}</TableCell>
                  <TableCell className="text-right text-sm text-amber-600">{r.kekurangan_25 ? fmtIDR(r.kekurangan_25) : "-"}</TableCell>
                  <TableCell><Badge className={`text-[10px] ${STATUS_STYLE[r.status] || "bg-taupe/20 text-wmuted"}`}>{r.status}</Badge></TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => setDrill(r)} data-testid={`recon-drill-${i}`}><Eye className="w-4 h-4" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Panel>

      <Panel>
        <div className="font-semibold text-cocoa mb-3">Histori Batch Import</div>
        {batches.length === 0 ? (
          <div className="text-center py-8 text-wmuted"><FileSpreadsheet className="w-8 h-8 mx-auto mb-2 opacity-40" />Belum ada batch.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>File</TableHead><TableHead>Periode Cair</TableHead>
                <TableHead className="text-right">Baris</TableHead><TableHead className="text-right">Baru</TableHead>
                <TableHead className="text-right">Diperbarui</TableHead><TableHead>HPP Belum Ketemu</TableHead>
                <TableHead>Oleh</TableHead><TableHead className="text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {batches.map((b) => {
                const missing = b.by_category?.pencairan?.hpp_missing || 0;
                return (
                  <TableRow key={b.id} data-testid={`recon-batch-${b.id}`}>
                    <TableCell className="text-sm font-medium text-cocoa max-w-[220px] truncate">{b.filename}</TableCell>
                    <TableCell className="text-sm text-wmuted">{fmtDateShort(b.period_start)} — {fmtDateShort(b.period_end)}</TableCell>
                    <TableCell className="text-right text-sm">{b.row_total}</TableCell>
                    <TableCell className="text-right text-sm text-status-passed">{b.inserted}</TableCell>
                    <TableCell className="text-right text-sm text-wmuted">{b.updated}</TableCell>
                    <TableCell className="text-sm">{missing > 0 ? <Badge className="bg-amber-100 text-amber-700 text-[10px]">{missing} order</Badge> : <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">lengkap</Badge>}</TableCell>
                    <TableCell className="text-sm text-wmuted">{b.uploaded_by || "-"}</TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => delBatch(b)} className="text-red-500 hover:text-red-600" data-testid={`recon-batch-del-${b.id}`}><Trash2 className="w-4 h-4" /></Button></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Panel>

      <TxnDialog open={!!drill} onClose={() => setDrill(null)} filters={filters} row={drill} />
    </div>
  );
}
